import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Psychology as AIIcon,
  FindInPage as ResumeIcon,
  Groups as InterviewIcon,
  TrendingUp as AnalyticsIcon,
  WorkOutline as JobIcon,
  AutoAwesome as AutomationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/atoms/Button';
import { useAuth } from '../hooks/useAuth';

/**
 * HomePage component
 * Modern landing page with hero section and feature highlights for HR recruitment platform
 */
export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: AIIcon,
      title: 'AI-Powered Screening',
      description:
        'Intelligent resume analysis and candidate matching using advanced AI algorithms to find the perfect fit.',
    },
    {
      icon: JobIcon,
      title: 'Smart Job Management',
      description:
        'Create, manage, and track job descriptions with AI-assisted content generation and optimization.',
    },
    {
      icon: InterviewIcon,
      title: 'Automated Interviews',
      description:
        'Schedule and conduct AI-powered interviews with intelligent question generation and candidate evaluation.',
    },
    {
      icon: ResumeIcon,
      title: 'Resume Intelligence',
      description:
        'Advanced parsing and analysis of resumes with skill extraction, experience mapping, and candidate ranking.',
    },
    {
      icon: AnalyticsIcon,
      title: 'Recruitment Analytics',
      description:
        'Comprehensive insights and metrics to optimize your hiring process and make data-driven decisions.',
    },
    {
      icon: AutomationIcon,
      title: 'End-to-End Automation',
      description:
        'Streamline your entire recruitment workflow from job posting to candidate onboarding with AI automation.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e22ce 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 3 }}
              >
                Transform Your Hiring with AI
              </Typography>
              <Typography variant="h5" component="p" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6 }}>
                SmartHire revolutionizes recruitment with AI-powered automation. From intelligent
                resume screening to automated interviews, manage your entire hiring process
                seamlessly.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {isAuthenticated ? (
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={() => navigate('/hr-dashboard')}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      color: '#1e3c72',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Go to Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={() => navigate('/login')}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        color: '#1e3c72',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/login')}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.8)',
                        color: 'white',
                        borderWidth: 2,
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderWidth: 2,
                        },
                      }}
                    >
                      Watch Demo
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: { xs: 200, md: 300 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 150, md: 200 },
                    height: { xs: 150, md: 200 },
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <AIIcon sx={{ fontSize: { xs: 80, md: 120 }, opacity: 0.8 }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}
        >
          Complete AI-Powered Recruitment Solution
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: '800px', mx: 'auto', lineHeight: 1.8 }}
        >
          Everything you need to streamline your hiring process, from job creation to candidate
          onboarding
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <feature.icon
                      sx={{
                        fontSize: 48,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)',
          py: 10,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 'bold', mb: 3, color: 'text.primary' }}
            >
              Ready to Transform Your Hiring?
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 5, lineHeight: 1.8, maxWidth: '600px', mx: 'auto' }}
            >
              Join leading companies using SmartHire to reduce hiring time by 60% and improve
              candidate quality with AI-powered insights.
            </Typography>
            {!isAuthenticated && (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                  }}
                >
                  Schedule Demo
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
